'use client';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion2';
import { Separator } from '@/components/ui/separator';
import { Slider } from '@/components/ui/slider';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Controle from '../../replay/Controle';
import { useAppSelector } from '@/app/hooks';
import { useVideoFallbackSource } from '@/hooks/useVideoFallbackSource';
import type { RootState } from '@/app/store';
import type { EvergreenTimelineEvent } from '@/features/webinar/types';
import {
  buildTimelineListEntries,
  buildTimelineSegments,
  getPurchaseNotificationMessage,
  isDisplayableTimelineEvent,
  resolveDisplayEventType,
  type TimelineDisplayEventType,
} from '@/features/webinar/evergreenTimelineUtils';
import { secondsToTimeString } from '@/lib/timelineOffset';

function secondsToFullTimeString(seconds: number): string {
  if (!Number.isFinite(seconds)) return '00:00:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

export type ContentLiveFormProps = {
  videoUrl: string;
  videoFallbackUrl?: string;
  // openTrigger?: number;
  isEdit?: boolean;
};

export default function ContentLiveForm(props: ContentLiveFormProps) {
  const {
    videoUrl,
    videoFallbackUrl,
    //  openTrigger = 0,
    isEdit = false,
  } = props;
  const { src: videoSrc, onError: onVideoError } = useVideoFallbackSource(
    videoUrl,
    videoFallbackUrl,
  );
  const { t } = useTranslation('createWebinar');
  const eventTypeSubtitle: Record<TimelineDisplayEventType, string> = useMemo(
    () => ({
      OFFER: t('contentLiveForm.eventTypes.offer'),
      QUIZ: t('contentLiveForm.eventTypes.quiz'),
      SURVEY: t('contentLiveForm.eventTypes.survey'),
      SHARED_RESOURCE: t('contentLiveForm.eventTypes.sharedResource'),
      PURCHASE_NOTIFICATION: t('contentLiveForm.eventTypes.purchaseNotification'),
    }),
    [t],
  );
  const scrollRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [videoTime, setVideoTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);

  const evergreenTimeline = useAppSelector((state: RootState) => state.webinar.evergreenTimeline);
  const offerTemplates = useAppSelector((state: RootState) => state.webinar.offerTemplates);
  const surveyTemplates = useAppSelector((state: RootState) => state.webinar.surveyTemplates);
  const quizTemplates = useAppSelector((state: RootState) => state.webinar.quizTemplates);
  const sharedResources = useAppSelector((state: RootState) => state.webinar.sharedResources);
  const timelineEvents = useMemo(
    () => evergreenTimeline.filter(isDisplayableTimelineEvent),
    [evergreenTimeline],
  );
  const timelineEntries = useMemo(() => buildTimelineListEntries(timelineEvents), [timelineEvents]);
  const getEventTitle = useCallback(
    (event: EvergreenTimelineEvent): string => {
      if (event.title) return event.title;
      const { resourceId } = event;
      const eventType = resolveDisplayEventType(event);
      if (eventType == null) return event.eventType;
      if (resourceId == null) {
        if (eventType === 'PURCHASE_NOTIFICATION') {
          const message = getPurchaseNotificationMessage(event.payloadJson);
          if (message) return message;
        }
        return eventTypeSubtitle[eventType];
      }
      switch (eventType) {
        case 'OFFER': {
          const offer = offerTemplates.find((o) => o.id === resourceId);
          return offer?.privateTitle ?? `${t('contentLiveForm.eventTypes.offer')} ${resourceId}`;
        }
        case 'SURVEY': {
          const survey = surveyTemplates.find((s) => s.id === resourceId);
          return survey?.title ?? `${t('contentLiveForm.eventTypes.survey')} ${resourceId}`;
        }
        case 'QUIZ': {
          const quiz = quizTemplates.find((q) => q.id === resourceId);
          return quiz?.name ?? `${t('contentLiveForm.eventTypes.quiz')} ${resourceId}`;
        }
        case 'SHARED_RESOURCE': {
          const resource = sharedResources.find((r) => r.id === resourceId);
          return (
            resource?.title ?? `${t('contentLiveForm.eventTypes.sharedResource')} ${resourceId}`
          );
        }
        // resourceId pointe l'offre dont l'achat est notifié.
        case 'PURCHASE_NOTIFICATION': {
          const offer = offerTemplates.find((o) => o.id === resourceId);
          return (
            offer?.privateTitle ??
            `${t('contentLiveForm.eventTypes.purchaseNotification')} ${resourceId}`
          );
        }
        default:
          return eventType;
      }
    },
    [offerTemplates, surveyTemplates, quizTemplates, sharedResources, eventTypeSubtitle, t],
  );
  const segments = useMemo(
    () => buildTimelineSegments(timelineEvents, videoDuration),
    [timelineEvents, videoDuration],
  );
  const [scrollHeight, setScrollHeight] = useState(0);
  const [clientHeight, setClientHeight] = useState(0);
  const [trackHeight, setTrackHeight] = useState(0);
  // const [accordionValue, setAccordionValue] = useState<string | undefined>(undefined);

  const dragStartY = useRef(0);
  const dragStartScrollTop = useRef(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);

  const playVideo = useCallback(() => {
    videoRef.current?.play();
    setIsPlaying(true);
  }, []);
  const pauseVideo = useCallback(() => {
    videoRef.current?.pause();
    setIsPlaying(false);
  }, []);
  const goBack = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.currentTime = Math.max(0, videoRef.current.currentTime - 10);
      setVideoTime(videoRef.current.currentTime);
    }
  }, []);
  const goForward = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.currentTime = Math.min(
        videoRef.current.duration,
        videoRef.current.currentTime + 10,
      );
      setVideoTime(videoRef.current.currentTime);
    }
  }, []);
  const refreshVideo = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      setVideoTime(0);
      videoRef.current.play();
      setIsPlaying(true);
    }
  }, []);
  const toggleMute = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(videoRef.current.muted);
    }
  }, []);

  const toggleFullscreen = useCallback(() => {
    const el = videoContainerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.() ??
        (el as HTMLElement & { webkitRequestFullscreen?: () => void }).webkitRequestFullscreen?.();
    } else {
      document.exitFullscreen?.() ??
        (document as Document & { webkitExitFullscreen?: () => void }).webkitExitFullscreen?.();
    }
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    return () => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
    };
  }, []);

  const updateScrollMetrics = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setScrollTop(el.scrollTop);
    setScrollHeight(el.scrollHeight);
    setClientHeight(el.clientHeight);
    const track = trackRef.current;
    if (track) setTrackHeight(track.clientHeight);
  }, []);

  useEffect(() => {
    const runAfterLayout = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(updateScrollMetrics);
      });
    };
    runAfterLayout();
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(updateScrollMetrics);
    ro.observe(el);
    if (trackRef.current) ro.observe(trackRef.current);
    return () => ro.disconnect();
  }, [updateScrollMetrics]);

  useEffect(() => {
    const id = requestAnimationFrame(() => updateScrollMetrics());
    return () => cancelAnimationFrame(id);
  }, [timelineEntries.length, updateScrollMetrics]);

  // useEffect(() => {
  //   if (accordionValue !== 'item-1') return;
  //   const t1 = setTimeout(updateScrollMetrics, 100);
  //   const t2 = setTimeout(updateScrollMetrics, 350);
  //   return () => {
  //     clearTimeout(t1);
  //     clearTimeout(t2);
  //   };
  // }, [accordionValue, updateScrollMetrics]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setScrollTop(el.scrollTop);
  }, []);

  const TRACK_PADDING = 2;
  const innerTrackHeight = Math.max(0, trackHeight - 2 * TRACK_PADDING);
  const canScroll = scrollHeight > clientHeight && innerTrackHeight > 0;
  const thumbHeight = canScroll
    ? Math.max(24, (clientHeight / scrollHeight) * innerTrackHeight)
    : 0;
  const maxThumbTop = innerTrackHeight - thumbHeight;
  const thumbTop =
    canScroll && maxThumbTop > 0
      ? TRACK_PADDING + (scrollTop / (scrollHeight - clientHeight)) * maxThumbTop
      : TRACK_PADDING;

  const handleTrackClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!scrollRef.current || !trackRef.current || !canScroll) return;
      const rect = trackRef.current.getBoundingClientRect();
      const y = e.clientY - rect.top - TRACK_PADDING;
      const ratio = Math.max(0, Math.min(1, y / innerTrackHeight));
      const newScrollTop = ratio * (scrollHeight - clientHeight);
      scrollRef.current.scrollTop = Math.max(0, newScrollTop);
    },
    [canScroll, innerTrackHeight, scrollHeight, clientHeight],
  );

  const handleThumbMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      if (!canScroll) return;
      dragStartY.current = e.clientY;
      dragStartScrollTop.current = scrollRef.current?.scrollTop ?? 0;
      const onMouseMove = (move: MouseEvent) => {
        const deltaY = move.clientY - dragStartY.current;
        const scrollable = scrollHeight - clientHeight;
        const trackScrollable = innerTrackHeight - thumbHeight;
        if (trackScrollable <= 0 || scrollable <= 0) return;
        const deltaScroll = (deltaY / trackScrollable) * scrollable;
        const newScroll = Math.max(
          0,
          Math.min(scrollHeight - clientHeight, dragStartScrollTop.current + deltaScroll),
        );
        scrollRef.current!.scrollTop = newScroll;
        dragStartY.current = move.clientY;
        dragStartScrollTop.current = newScroll;
      };
      const onMouseUp = () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    },
    [canScroll, scrollHeight, clientHeight, innerTrackHeight, thumbHeight],
  );

  return (
    <Accordion type="single" collapsible>
      <AccordionItem value="item-1" className={`bg-white p-0 ${isEdit ? 'accordion-page' : ''}`}>
        <AccordionTrigger className=" rounded-[20px]">
          {t('contentLiveForm.accordionTitle')}
        </AccordionTrigger>
        <AccordionContent
          className={`bg-medium p-4 rounded-lg flex flex-col gap-10 overflow-visible ${isEdit ? 'mb-4' : ''}`}
        >
          <div className="flex items-center w-full justify-between gap-6">
            <div
              ref={videoContainerRef}
              className="w-1/3 h-[350px] border border-input rounded-lg bg-white relative overflow-hidden"
            >
              <video
                ref={videoRef}
                className="w-full h-full object-cover rounded-lg bg-black"
                src={videoSrc}
                onError={onVideoError}
                muted={isMuted}
                playsInline
                loop
                onTimeUpdate={() => videoRef.current && setVideoTime(videoRef.current.currentTime)}
                onLoadedMetadata={() =>
                  videoRef.current && setVideoDuration(videoRef.current.duration)
                }
              />
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10">
                <Controle
                  onPlay={playVideo}
                  onPause={pauseVideo}
                  onGoBack={goBack}
                  onGoForward={goForward}
                  onRefresh={refreshVideo}
                  onToggleMute={toggleMute}
                  onFullscreen={toggleFullscreen}
                  isPlaying={isPlaying}
                  isMuted={isMuted}
                  showGoBack={false}
                  showGoForward={false}
                  showMute={false}
                />
              </div>
            </div>
            <div className="w-2/3 h-[350px] flex min-h-0 gap-2 ">
              <div className="flex flex-col flex-1 min-w-0">
                <div className="flex items-center bg-subtitle p-4 rounded-t-lg shrink-0">
                  <div className="text-sm text-secondary w-20 shrink-0">
                    {t('contentLiveForm.colTime')}
                  </div>
                  <div className="text-sm text-secondary flex-1 min-w-0">
                    {t('contentLiveForm.colEvent')}
                  </div>
                </div>
                <div
                  ref={scrollRef}
                  onScroll={handleScroll}
                  className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden rounded-b-lg bg-white [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                >
                  {timelineEntries.map(({ key, event, offsetSeconds, isHide }) => {
                    const displayEventType = resolveDisplayEventType(event);
                    const subtitle = displayEventType ? eventTypeSubtitle[displayEventType] : '';
                    return (
                      <div key={key} className="flex items-center p-4 ">
                        <div className="text-sm text-secondary w-20 shrink-0">
                          {secondsToTimeString(offsetSeconds)}
                        </div>
                        <div className="text-sm text-secondary flex-1 flex items-center gap-2 min-w-0">
                          <div className="flex items-start gap-2 min-w-0">
                            <div
                              className={`size-2.5 mt-1 rounded-full shrink-0 ${isHide ? 'bg-red-500' : 'bg-green-500'}`}
                            />
                            <div className="min-w-0">
                              <div className="text-black text-sm truncate">
                                {getEventTitle(event)}
                              </div>
                              <p className="text-xs text-secondary">{subtitle}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div
                ref={trackRef}
                role="scrollbar"
                aria-valuenow={
                  scrollHeight > 0
                    ? Math.round((scrollTop / (scrollHeight - clientHeight)) * 100)
                    : 0
                }
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={t('contentLiveForm.scrollbarAriaLabel')}
                className="w-2 h-full min-h-0 shrink-0 bg-white rounded-lg relative cursor-pointer py-2 box-border"
                onClick={handleTrackClick}
              >
                {canScroll && (
                  <div
                    role="button"
                    tabIndex={0}
                    className="absolute left-1/2 -translate-x-1/2 w-1.5  bg-[#C0D3FF] rounded-full cursor-grab active:cursor-grabbing"
                    style={{ height: thumbHeight, minHeight: 24, top: thumbTop }}
                    onMouseDown={handleThumbMouseDown}
                    onKeyDown={(e) => {
                      if (e.key === 'ArrowDown') {
                        scrollRef.current?.scrollBy({ top: 40 });
                      } else if (e.key === 'ArrowUp') {
                        scrollRef.current?.scrollBy({ top: -40 });
                      }
                    }}
                  />
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center w-full justify-between gap-6 overflow-visible">
            <div className="w-full shrink-0 relative overflow-visible">
              <div className="relative overflow-visible">
                <div
                  className="absolute -top-7 text-sm text-secondary pointer-events-none z-10 whitespace-nowrap px-1.5 py-0.5"
                  style={{
                    left: `clamp(1.5rem, ${((videoTime || 0) / (videoDuration || 1)) * 100}%, calc(100% - 1.5rem))`,
                    transform: 'translate(-50%, 0)',
                  }}
                >
                  {secondsToTimeString(videoTime)}
                </div>
                <Slider
                  min={0}
                  max={videoDuration || 100}
                  value={[videoTime]}
                  onValueChange={([v]) => {
                    const t = v ?? 0;
                    if (videoRef.current) {
                      videoRef.current.currentTime = t;
                      setVideoTime(t);
                    }
                  }}
                />

                {videoDuration > 0 &&
                  segments.map((segment) => {
                    const leftPercent = (segment.startSeconds / videoDuration) * 100;
                    const widthPercent =
                      ((segment.endSeconds - segment.startSeconds) / videoDuration) * 100;
                    if (!segment.instant && widthPercent <= 0) return null;

                    return (
                      <Tooltip key={segment.id}>
                        <TooltipTrigger asChild>
                          <div
                            className="absolute top-1/2 h-4 min-w-[4px] z-0 cursor-default pointer-events-auto"
                            style={{
                              left: `${leftPercent}%`,
                              width: `${widthPercent}%`,
                              transform: 'translateY(-50%)',
                            }}
                          >
                            <div
                              className="h-full w-full bg-accent rounded-sm opacity-80 pointer-events-none"
                              aria-hidden
                            />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent
                          side="top"
                          className="bg-black text-white border-0 flex flex-col gap-2 p-3"
                          arrowClassName="!bg-black !fill-black"
                        >
                          <span className="text-sm font-medium whitespace-nowrap">
                            {getEventTitle(segment.event)}
                          </span>
                          <Separator className="bg-white/30" />
                          <span className="text-xs text-white/90">
                            {segment.instant
                              ? secondsToFullTimeString(segment.startSeconds)
                              : `${secondsToFullTimeString(segment.startSeconds)}-${secondsToFullTimeString(segment.endSeconds)}`}
                          </span>
                        </TooltipContent>
                      </Tooltip>
                    );
                  })}
              </div>

              <div className="flex items-center justify-between w-full mt-4">
                <div className="text-sm text-secondary">{secondsToTimeString(videoTime)}</div>
                <div className="text-sm text-secondary">{secondsToTimeString(videoDuration)}</div>
              </div>
            </div>
            {/* <Button>
              <Plus />
              Ajouter un évènement
            </Button> */}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
